/**
 * Customer Registration Form Vue Component
 * ------------------------------------------------------------------------------
 * Liquid template in templates/pages-customer-registration-form.liquid
 * Standalone template in __dist-customer-rego-form/customer-registration-form.html
 * 
 * - handle response data
 *   - fix up submission errors and make it submit happily
 *   - error messages
 *   - show user XXX values from response
 * - update standalone page html/header/footer
 */

import axios from 'axios';
import Vue from 'vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Vuelidate from 'vuelidate';
import parseMobile from 'libphonenumber-js/mobile';
import VueMask from 'v-mask'

// import { getCustomerRegistrationFormUrl, xhrRequestConfig } from '../utils/api';
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators';

Vue.use(Vuelidate);
Vue.use(VueMask);

const baseUrl = window.location.host

const API_URL_DEV = `https://1001-optical-development.myshopify.com/apps/optomateuat/api/2021-01/optomate/patient?shop=1001-optical-development.myshopify.com`;
const API_URL_PROD = `https://${baseUrl}/apps/optomateapp/api/2021-01/optomate/patient?shop=1001-optical.myshopify.com`;
const xhrRequestConfig = {
    headers: {
        'X-Requested-With': 'XMLHttpRequest'
    }
};

export default {
    components: {
        DatePicker
    },

    data() {
        return {
            // General
            isSubmitting: false,
            showForm: true,
            showResponse: false,

            // Errors from Middleware
            showErrorMessages: false,
            // errorMessageTitle: false,
            errorMessages: "There was an error, please check the form values and try again",
            userID: null,
            showSuccess: false,

            maxMinDate: new Date(),
        
            showStore: false,
            store: null,
            title: null,
            firstname: null,
            lastname: null,
            date_of_birth: null, //YYYY-MM-DD
            gender: null, // F, M, U
            email: null,
            mobile: null,
            postcode: null,
            
            is_eye_test: false,
            medicare_number: null, 
            medicare_refno: null,
            medicare_expiry: null,
            healthfund_identifier: null,
            have_healthfund: false,

            communication_preference: false,

            customer_registration_text: {
                general: {
                    title: "Customer Registration Form",
                    button: "Submit",
                    popup_success_message: "Thank you for registering",
                    popup_message_user: "Your customer ID is: "
                },
                fields: {
                    label_store: "Store",
                    label_title: "Title",
                    label_first_name: "First Name",
                    label_last_name: "Last Name",
                    label_dob: "Date of Birth",
                    label_gender: "Gender",
                    label_email: "Email Address",
                    label_mobile: "Mobile Number",
                    label_postcode: "Postcode",
                    label_here_for_eye_test: "Are you here for an eye test?",
                    label_medicare_num: "Medicare Number",
                    label_medicare_ref: "Medicare Reference",
                    label_medicare_expiry: "Medicare Expiry",
                    label_have_health_fund: "Do you have a Health Fund?",
                    label_health_fund: "Health Fund",
                    label_comm_preference: "I want to sign up to the newsletter"
                },
                errors: {
                    submit_error_title: "There was an error with your submission",
                    required_store: "Store is required",
                    required_title: "Personal title is required",
                    required_first_name: "First name is required",
                    required_last_name: "Last name is required",
                    required_date_of_birth: "Date of birth is required",
                    error_date_of_birth: "Please select a valid date of birth",
                    required_gender: "Gender is required",
                    required_email: "Email is required",
                    error_email: "Please enter a valid email address",
                    required_mobile: "Mobile number is required",
                    mobile_error: "Please enter a valid Aus mobile number",
                    required_postcode: "Postcode is required",
                    error_postcode: "Postcode must be 4 digits",

                    required_medicare_number: "Please enter your Medicare card number",
                    error_medicare_number: "Please enter a valid Medicare card number",
                    required_medicare_expiry: "Please enter your Medicare card expiry date",
                    error_medicare_expiry: "Please enter a valid expiry date",
                    required_medicare_refno: "Please enter your Medicare card reference number",
                    required_health_fund: "Please select a Health Fund",
                }
            }
        }
    },

    computed: {
        apiUrlValue() {
            if (!location.host.includes('1001-optical')) {
                if (location.host.includes('1001landing-dev')) return API_URL_DEV;
                else return API_URL_PROD;
            } else {
                return API_URL_DEV;
            }
        }
    },

    validations() {
        /*
            As per the docs, the validation rules can be made "smart", and have different fields 
            be required dynamically, based on the values of other fields ( using required: requiredIf ) 
            
            However, it was not behaving at all when trying to make it work that way, but the verbose 
            structure works fine
        */
        if (!this.is_eye_test) {
            return {
                store: {
                    required
                },
                title: {
                    required
                },
                firstname: {
                    required
                },
                lastname: {
                    required
                },
                date_of_birth: {
                    required,
                    isValidDate: (value) => { // must be a past date
                        
                        if (!value) return false;

                        let currentDateParsed = new Date(),
                            selectedDateParsed = new Date(value);

                       return selectedDateParsed < currentDateParsed; // selected date is valid if it is in the past
                    }
                },
                gender: {
                    required
                },
                email: {
                    required,
                    email
                },
                mobile: {
                    required,
                    mobileNumValid: (value) => {
                        if (!value) return false;
                        let phone = parseMobile(value, 'AU');
                        return !!phone ? phone.isValid() : false;
                    }
                },
                postcode: {
                    required,
                    minLength: minLength(4),
                    maxLength: maxLength(4)
                }
            }
        } else if(!!this.is_eye_test && !this.have_healthfund) {
            return {
                store: {
                    required
                },
                title: {
                    required
                },
                firstname: {
                    required
                },
                lastname: {
                    required
                },
                date_of_birth: {
                    required,
                    isValidDate: (value) => { // must be a past date
                        
                        if (!value) return false;

                        let currentDateParsed = new Date(),
                            selectedDateParsed = new Date(value);

                       return selectedDateParsed < currentDateParsed; // selected date is valid if it is in the past
                    }
                },
                gender: {
                    required
                },
                email: {
                    required,
                    email
                },
                mobile: {
                    required,
                    mobileNumValid: (value) => {
                        if (!value) return false;
                        let phone = parseMobile(value, 'AU');
                        return !!phone ? phone.isValid() : false;
                    }
                },
                postcode: {
                    required,
                    minLength: minLength(4),
                    maxLength: maxLength(4)
                },
                medicare_number: {
                    required,
                    minLength: minLength(12),
                    maxLength: maxLength(12)
                },
                medicare_expiry: {
                    required,
                    isValidDate: (value) => { // must be a non-expired date
                        
                        if (!value) return false;

                        let current = new Date(),
                            currentMonth = `${current.getMonth() + 1}`.length === 1 ? `0${current.getMonth() + 1}` : current.getMonth() + 1,
                            currentYear = current.getFullYear(),
                            currentDateParsed = new Date(`${currentYear}-${currentMonth}`),
                            selectedDateParsed = new Date(`${value.split('-')[1]}-${value.split('-')[0]}`);

                       return selectedDateParsed >= currentDateParsed; // selected date is valid if it is current month or the future
                    }
                },
                medicare_refno: {
                    required
                }
            }

        } else if (!!this.is_eye_test && !!this.have_healthfund) {
            return {
                store: {
                    required
                },
                title: {
                    required
                },
                firstname: {
                    required
                },
                lastname: {
                    required
                },
                date_of_birth: {
                    required,
                    isValidDate: (value) => { // must be a past date
                        
                        if (!value) return false;

                        let currentDateParsed = new Date(),
                            selectedDateParsed = new Date(value);

                       return selectedDateParsed < currentDateParsed; // selected date is valid if it is in the past
                    }
                },
                gender: {
                    required
                },
                email: {
                    required,
                    email
                },
                mobile: {
                    required,
                    mobileNumValid: (value) => {
                        if (!value) return false;
                        let phone = parseMobile(value, 'AU');
                        return !!phone ? phone.isValid() : false;
                    }
                },
                postcode: {
                    required,
                    minLength: minLength(4),
                    maxLength: maxLength(4)
                },
                medicare_number: {
                    required,
                    minLength: minLength(12),
                    maxLength: maxLength(12)
                },
                medicare_expiry: {
                    required,
                    isValidDate: (value) => { // must be a non-expired date
                        
                        if (!value) return false;

                        let current = new Date(),
                            currentMonth = current.getMonth() + 1,
                            currentYear = current.getFullYear(),
                            currentDateParsed = new Date(`${currentYear}-${currentMonth}`),
                            selectedDateParsed = new Date(`${value.split('-')[1]}-${value.split('-')[0]}`);

                       return selectedDateParsed >= currentDateParsed; // selected date is valid if it is current month or the future
                    }
                },
                medicare_refno: {
                    required
                },
                healthfund_identifier: {
                    required
                }
            }
        }
    },

    watch: {
        is_eye_test(value) {
            // reset the values if closed
            if (!value) {
                this.medicare_number = null;
                this.medicare_refno = null;
                this.medicare_expiry = null;
                this.healthfund_identifier = null;
                this.have_healthfund = false;
            }
        },
        have_healthfund(value) {
            // reset the healthfund selected if the sub form gets hidden and a healthfund has been selected
            if (!value && !!this.healthfund_identifier) {
                this.healthfund_identifier = null;
            }   
        }
    },

    created() {
    },

    mounted() {
        let storeId = new URL(window.location).searchParams.get('store-identifier');

        if (!!storeId) {
            this.store = storeId.toUpperCase();
        } else {
            this.showStore = true;
        }
    },

    methods: {

        ////////////////////////////////////////////
        // PRIVATE
        ////////////////////////////////////////////
        _validation() {
            window.scroll({ top: 0, left: 0, behavior: 'smooth' });
            this._handleErrors(false);        
            this.$v.$touch();
            return !this.$v.$error;
        },

        _handleErrors(show,data) {
            this.errorMessages="There was an error, please check the form values and try again"
            if(data?.email) {
                this.errorMessages = "Please use a unique email address. The one you entered already exists.";
            }
            if (!!show) {
                // this.errorMessages = data;
                this.showErrorMessages = true;
                // window.scroll({ top: 0, left: 0, behavior: 'smooth' });
            } else {
                // this.errorMessages = [];
                this.showErrorMessages = false;
            }
        },

        _postForm() {
            let mobileNum = parseMobile(this.mobile, 'AU'),
                medicareExpireDate = !!this.is_eye_test ? this.medicare_expiry.split('-') : null,
                formData = {
                "store": this.store,
                "title": this.title,
                "firstname": this.firstname,
                "lastname": this.lastname,
                "date_of_birth": this.date_of_birth,
                "gender": this.gender.toUpperCase(), 
                "email": this.email,
                "mobile": `0${mobileNum.nationalNumber}`, // parse as National Number as per libphonenumber-js docs, and prefix 0
                "postcode": this.postcode,
                "is_eye_test": this.is_eye_test,
                "medicare_number": this.medicare_number, 
                "medicare_refno": this.medicare_refno,
                "medicare_expiry": !!medicareExpireDate ? `${medicareExpireDate[0]}/${medicareExpireDate[1].slice(2)}` : this.medicare_expiry,
                "healthfund_identifier": this.healthfund_identifier,
                "no_healthfund": !this.have_healthfund,
                "communication_preference" : this.communication_preference
            };

            axios.post(this.apiUrlValue, formData, xhrRequestConfig)
                // .then( response => response.json )
                .then( response => {
                    if (!response.data.success) {
                        if (!!response.data.message) {
                            if (response.data.message.toLowerCase().includes('medicare_number')) this.errorMessages = 'Invalid Medicare number';
                        } else if (!!response.data.email) {
                            this.errorMessages = 'The email already exist.';
                        };
                        this._handleErrors(true);
                    } else if (!!response.data.success) {
                        this.userID = !!response.data.optomate_data ? response.data.optomate_data.ID : null;
                        this.successMessage = !!response.data.message ? response.data.message : null
                        this.showSuccess = true;
                    }
                    this.isSubmitting = false; 
                })
                .catch( error => {
                    console.error(error);
                    this._handleErrors(true,error.response.data);
                    this.isSubmitting = false;
                });
        },
        
        ////////////////////////////////////////////
        // PUBLIC
        ////////////////////////////////////////////

        formSubmit() {
            this.isSubmitting = true;
            if (this._validation()) { 
               this._postForm();
            } else {
                this.isSubmitting = false;
            }
        },

        medicareNumberInput($event) {
            const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

            let keyPressed = $event.key;

            if (!keysAllowed.includes(keyPressed)) {
                $event.preventDefault();
            }
        },

        closePopup() {
            // just reload the page instead of having to clear and reset everything
            location.reload();
        }
    }
}